import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  MdGroup,
  MdOutlineArrowBackIosNew,
  MdSpeed,
} from "react-icons/md";
import { BsFuelPumpFill } from "react-icons/bs";
import { TbEngine } from "react-icons/tb";
import { GiGearStick } from "react-icons/gi";

import AdSlideshow from "../../../components/main-layout-components/ad-slideshow/AdSlideshow";
import AdDetails from "../../../components/main-layout-components/ad-details/AdDetails";
import useApiService from "../../../hooks/useApiService";
import { getClassifiedById } from "../../../services/main-service/mainService";
import Spinner from "../../../components/common/spinner/Spinner";

interface SpecRow {
  Icon: React.ComponentType<any>;
  label: string;
  value: string | number | undefined | null;
}

function SpecItem({ Icon, label, value }: SpecRow) {
  if (!value && value !== 0) return null;
  return (
    <div
      className="d-flex justify-content-between align-items-center py-2"
      style={{ borderBottom: "1px solid #f1f5f9", fontSize: "0.9rem" }}
    >
      <span style={{ color: "#64748b", display: "flex", alignItems: "center", gap: 8 }}>
        <Icon size={15} color="#ed1b24" /> {label}
      </span>
      <span style={{ fontWeight: 600, color: "#1e293b" }}>{value}</span>
    </div>
  );
}

function AdPage() {
  const { adId } = useParams();
  const navigate = useNavigate();

  const { data: adDetails, loading, error } = useApiService({
    serviceCall: () => getClassifiedById({ classified_id: adId }),
    dependencies: [],
  });

  if (loading) return <Spinner />;
  if (error)   return <p className="text-center py-5">{error}</p>;

  return (
    <div className="py-4 bg-body-secondary">
      <div className="container">
        <div className="maximum-width mx-auto">

          {/* Back + Title */}
          <div
            className="d-flex align-items-center gap-2 mb-3 cursor-pointer"
            onClick={() => navigate(-1)}
            style={{ width: "fit-content" }}
          >
            <MdOutlineArrowBackIosNew size={20} color="#ed1b24" />
            <span style={{ fontSize: "0.875rem", color: "#64748b", fontWeight: 500 }}>
              Back to listings
            </span>
          </div>

          <h4
            className="mb-3 fw-bold"
            style={{ color: "#1e293b", letterSpacing: "-0.02em" }}
          >
            {adDetails?.model} {adDetails?.make} {adDetails?.year}
          </h4>

          {/* Main Content Grid */}
          <div className="row g-3">

            {/* Left col: slideshow + specs + description */}
            <div className="col-sm-12 col-md-8">

              {/* Slideshow */}
              {(adDetails?.image_urls?.length ?? 0) > 0 && (
                <div
                  className="mb-3"
                  style={{
                    borderRadius: 14,
                    overflow: "hidden",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                    background: "#fff",
                  }}
                >
                  <AdSlideshow slideImages={adDetails?.image_urls ?? []} />
                </div>
              )}

              {/* Specifications */}
              <div
                className="bg-white p-3 mb-3"
                style={{ borderRadius: 14, border: "1.5px solid #e8edf2", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
              >
                <p className="section-label mb-2">Vehicle Specifications</p>
                <SpecItem Icon={MdGroup}        label="No. of Owners"  value={adDetails?.number_of_owners} />
                <SpecItem Icon={MdSpeed}        label="Mileage"        value={adDetails?.mileage ? `${adDetails.mileage.toLocaleString()} km` : ""} />
                <SpecItem Icon={GiGearStick}    label="Transmission"   value={adDetails?.transmission} />
                <SpecItem Icon={BsFuelPumpFill} label="Fuel Type"      value={adDetails?.fuel} />
                <SpecItem Icon={TbEngine}       label="Engine"         value={adDetails?.engine_cc ? `${adDetails.engine_cc.toLocaleString()} cc` : ""} />
              </div>

              {/* Description */}
              {adDetails?.description && (
                <div
                  className="bg-white p-3"
                  style={{ borderRadius: 14, border: "1.5px solid #e8edf2", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
                >
                  <p className="section-label mb-2">Description</p>
                  <p
                    className="white-space-pre-line"
                    style={{ fontSize: "0.9rem", color: "#374151", lineHeight: 1.7, margin: 0 }}
                  >
                    {adDetails.description}
                  </p>
                </div>
              )}
            </div>

            {/* Right col: price + seller */}
            <div className="col-sm-12 col-md-4">
              <div style={{ position: "sticky", top: 90 }}>
                <AdDetails adDetails={adDetails} />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default AdPage;
